import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PasswordInput from "@/pages/components/input-password";
import { Button } from "@/components/ui/button";
import GroupInfo from "@/pages/group-info";
import { InputWithLabel } from "@/pages/components/input-label";
import { useForm } from "@tanstack/react-form";
import { loginPageSchema } from "@/lib/form-schema";
import { useLogin } from "@/hooks/use-login";
import { useGroupProvider } from "@/context/group-provider";
import type { IAuthData } from "@/context/auth-provider";
import useAuth, { AuthMode } from "@/context/auth-provider";
import ScreenRouteNames from "@/constants/screen-route-names";
import { ErrorNotificationType } from "./components/error-notification";
import type { IUser, IPwdExpiryResponse } from "@/model/user";
import { useNotificationError } from "@/context/error-provider";
import { Checkbox } from "@/components/ui/checkbox";
import type { AxiosError } from "axios";
import type { IAccountResetResponse } from "@/hooks/use-account-reset";
import { usePasswordReset } from "@/hooks/use-account-reset";
import { useTheme } from "@/context/theme-provider";
import { STORAGE, STORAGE_KEY } from "@/constants";
import { getItem, setItem } from "@/lib/storage-utils";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { usePanelLogger } from "@/services/logger/logger-hooks";
import { useFocusOnOpen } from "@/hooks/use-focus-on-open";
import IDENTIFIERS from "@/constants/test_id";
import { PanelTracker } from "@/services/logger/smart-context-provider";
import { THIRD_PARTY_INTEGRATION, getEbixUrl } from "@/lib/client-utils";
import { lowerCaseComparison } from "@/lib/string-util";
import { cn, isEmpty, describeMissingFields } from "@/lib/utils";

type ILoginRequestValues = {
  username: string;
  password: string;
  loginGroupId: string;
  divisionId: string;
  groupId: string;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { addError } = useNotificationError();
  const loginQuery = useLogin();
  const pwdResetQuery = usePasswordReset();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  /**
   * The "Need Help?" trigger unmounts while Group Info is shown (it's the other branch
   * of the same conditional), so a ref captured at click time would point at a detached
   * node by the time we could restore focus to it — calling .focus() on it is a silent
   * no-op and focus falls to <body> (2.4.3 Focus Order — A). Instead, closing only
   * records that focus is owed; the effect below restores it once the form section
   * (and the real, freshly-mounted trigger inside it) is back in the DOM.
   */
  const formSectionRef = useRef<HTMLDivElement>(null);
  const restoreFocusOnCloseRef = useRef(false);
  const groupInfoRef = useRef<HTMLDivElement>(null);
  const { logPanelOpen, logPanelClose } = usePanelLogger();
  const openPanel = () => {
    restoreFocusOnCloseRef.current = true;
    setIsPanelOpen(true);
    logPanelOpen(
      IDENTIFIERS.LOGIN.NEED_HELP_LINK,
      { panelName: "Group Info" },
      {
        subCategory: LogSubCategories.GROUP_INFO_MODAL,
        type: "PAGE_VIEW",
      },
    );
  };
  const closePanel = () => {
    setIsPanelOpen(false);
    logPanelClose(
      IDENTIFIERS.LOGIN.NEED_HELP_LINK,
      { panelName: "Group Info" },
      {
        subCategory: LogSubCategories.GROUP_INFO_MODAL,
        type: "PAGE_VIEW",
      },
    );
  };
  useEffect(() => {
    if (isPanelOpen || !restoreFocusOnCloseRef.current) return;
    restoreFocusOnCloseRef.current = false;
    formSectionRef.current
      ?.querySelector<HTMLElement>(
        `[data-testid="${IDENTIFIERS.LOGIN.NEED_HELP_LINK}"]`,
      )
      ?.focus();
  }, [isPanelOpen]);
  // 2.4.3 Focus Order — A: moves focus into Group Info when it replaces the form, and
  // lets Escape act as a shortcut back — not a focus trap, since nothing here is a
  // modal overlay blocking the rest of the page.
  useFocusOnOpen(groupInfoRef, isPanelOpen, closePanel);

  const { groupData } = useGroupProvider();
  const { changeTheme } = useTheme();
  const { updateAuthData } = useAuth();

  const cachedGroupDetail = getItem(
    STORAGE_KEY.CACHED_GROUP_DETAIL,
    STORAGE.LOCAL,
  );

  const isEbixClient = lowerCaseComparison(
    groupData?.login_protocol,
    THIRD_PARTY_INTEGRATION.EBIX,
  );

  const form = useForm({
    defaultValues: {
      userName: !cachedGroupDetail?.rememberedUsername
        ? ""
        : cachedGroupDetail?.rememberedUsername,
      password: "",
      isRemembered: false,
    },
    onSubmit: ({ value }) => {
      const userInfo: Omit<ILoginRequestValues, "password"> = {
        username: value.userName,
        groupId: groupData!.group_id,
        loginGroupId: groupData!.entered_id,
        divisionId: groupData!.division_id,
      };

      handleLogin(userInfo);
    },
  });

  // Sync theme from session storage (after loader sets PRODUCT_CONFIG)
  useEffect(() => {
    const productConfig = getItem(STORAGE_KEY.PRODUCT_CONFIG, STORAGE.SESSION);
    if (productConfig?.theme) {
      changeTheme(productConfig.theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Panel context is handled by PanelTracker mount/unmount.

  // Reset form and clear errors on mount or location change
  // useEffect(() => {
  //   form.reset();
  //   // clearErrors();
  //   queryClient.resetQueries({ queryKey: ["login"] });
  //   queryClient.resetQueries({ queryKey: ["passwordReset"] });
  // }, [location, form, queryClient]);

  const handleLogin = (userInfo: Omit<ILoginRequestValues, "password">) => {
    if (!groupData) return;

    loginQuery.mutate(
      {
        username: form.state.values.userName,
        password: form.state.values.password,
        loginGroupId: groupData.entered_id,
        groupId: groupData.group_id,
      },
      {
        onSuccess: async (user: IUser) => {
          const authData: IAuthData = {
            user,
            mode: AuthMode.MEMBER,
          };

          await updateAuthData(authData);

          if (form.state.values.isRemembered) {
            setItem(
              STORAGE_KEY.CACHED_GROUP_DETAIL,
              {
                ...cachedGroupDetail,
                rememberedUsername: form.state.values.userName,
              },
              STORAGE.LOCAL,
            );
          }

          if (user.is_mfa_enabled) {
            addError({
              message: user.message,
              type: ErrorNotificationType.INFO,
            });
            navigate(`/${ScreenRouteNames.MFA_LOGIN.path}`, {
              state: {
                username: form.state.values.userName,
                signature: user.signature,
              },
              replace: true,
            });
          } else {
            navigate(`/${ScreenRouteNames.HOME.path}`, { replace: true });
          }
        },

        onError: (error) => {
          const axiosError = (error as AxiosError).response;
          const errorResponse = axiosError?.data as IPwdExpiryResponse;
          if (errorResponse?.error === "REQ-401") {
            pwdResetQuery.mutate(userInfo, {
              onSuccess: (data: IAccountResetResponse) => {
                if (!data.success) return;
                navigate(`/${ScreenRouteNames.VERIFICATION.path}`, {
                  state: {
                    userInfo,
                    signature: data.signature,
                  },
                });
              },
            });
          }
        },
      },
    );
  };

  function onForgotPassword() {
    if (isEbixClient && groupData?.client_code) {
      window.open(getEbixUrl(groupData?.client_code), "_blank");
    } else {
      navigate(`/${ScreenRouteNames.ACCOUNT_RESET.path}`);
    }
  }

  function onRegister() {
    if (isEbixClient && groupData?.client_code) {
      window.open(getEbixUrl(groupData.client_code), "_blank");
    } else {
      navigate(`/${ScreenRouteNames.REGISTER.path}`);
    }
  }

  return (
    <main className="flex h-full flex-col items-center justify-center px-10">
      {isPanelOpen ? (
        // 4.1.2 Name, Role, Value — A / 2.4.3 Focus Order — A: Group Info replaces the
        // login form in place — no slide-up panel, no overlay, no dialog semantics,
        // because nothing here is a modal floating over the page. useFocusOnOpen moves
        // focus into it (GroupInfo's own <h1> "Group Information" becomes the page's
        // heading while this is shown) and wires Escape as a shortcut back to the form.
        <div ref={groupInfoRef} className="w-full">
          <PanelTracker
            panelName="Group Info"
            category={LogCategories.NONE}
            subCategory={LogSubCategories.GROUP_INFO_MODAL}
          >
            <GroupInfo onClose={closePanel} />
          </PanelTracker>
        </div>
      ) : (
        // display: contents keeps this wrapper out of the flex layout while still
        // giving the focus-restoration effect above a DOM node to query the trigger
        // back out of. space-y lives here (not on <main>) because Tailwind's sibling
        // selector matches real DOM children — display: contents only flattens the
        // box tree for flex layout, it doesn't change DOM adjacency.
        <div ref={formSectionRef} className="contents space-y-6">
          {/* <header className="absolute left-0 top-0 flex w-full items-center justify-center py-4">
            <img src={logo} alt="Login" className="w-40 object-cover" />
          </header> */}
          {/* 1.3.1 Info and Relationships — A / 2.4.6 Headings and Labels — AA: the page
              had no heading at all. Kept visually hidden so the design is unchanged. */}
          <h1 className="sr-only">Sign in to your account</h1>

          <div className="h-16 max-w-40">
            {/* 1.1.1 Non-text Content — A: alt="Login" described neither the image nor
                its meaning, and the element rendered with src={undefined} when no logo
                was supplied. The logo is decorative branding. */}
            {Boolean(groupData?.client_logo) && (
              <img
                src={`data:image/png;base64,${groupData?.client_logo}`}
                alt=""
                className="h-full object-contain"
              />
            )}
          </div>

          {/* 1.3.1 — A: this was a <label> with no associated control. It is body text. */}
          <p
            className={`${
              isEmpty(groupData?.protocolMessage) ? "hidden" : "block"
            } text-center text-sm font-bold`}
          >
            {groupData?.protocolMessage}
          </p>
          <form
            className="flex min-w-[80%] flex-col space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="userName"
              validators={{
                // TanStack Form runs every configured validator (regardless of its own
                // lifecycle tag) on a submit attempt, so this onBlur validator already
                // populates the error below even for a member who tabbed straight to
                // the Login button without ever blurring the field — no separate
                // onSubmit validator needed (confirmed empirically, not assumed).
                onBlur: ({ value }) => {
                  const result =
                    loginPageSchema.shape.userName.safeParse(value);
                  return result.success
                    ? undefined
                    : result.error.errors[0].message;
                },
              }}
            >
              {(field) => (
                <div>
                  <InputWithLabel
                    id="userName"
                    // 3.3.2 Labels or Instructions — A: was commented out, leaving only a
                    // placeholder. Labelled "Username" to match the field and its
                    // autoComplete="username" (the old commented-out value said "Email
                    // Address", which contradicted both).
                    label="Username"
                    placeholder="Username"
                    value={field.state.value}
                    onChange={(newValue) => field.handleChange(newValue)}
                    onBlur={() => field.handleBlur()}
                    error={field.state.meta.errors[0]}
                    data-testid={IDENTIFIERS.LOGIN.USERNAME_INPUT}
                    autoComplete="username"
                  />
                </div>
              )}
            </form.Field>

            <div className="relative mb-4 flex flex-col gap-2">
              <form.Field
                name="password"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      loginPageSchema.shape.password.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div>
                    <PasswordInput
                      id="password"
                      // 3.3.2 Labels or Instructions — A
                      label="Password"
                      placeholder="Password"
                      value={field.state.value}
                      onChange={(newValue) => field.handleChange(newValue)}
                      onBlur={() => field.handleBlur()}
                      error={field.state.meta.errors[0]}
                      data-testid={IDENTIFIERS.LOGIN.PASSWORD_INPUT}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="my-6 flex items-center justify-between">
              <form.Field name="isRemembered">
                {(field) => (
                  <label className="flex items-center justify-center space-x-1.5">
                    <Checkbox
                      id="rememberMe"
                      checked={field.state.value}
                      onCheckedChange={(checked) =>
                        field.handleChange(Boolean(checked))
                      }
                      data-testid={IDENTIFIERS.LOGIN.REMEMBER_ME_CHECKBOX}
                    />
                    {/*
                  1.3.1 Info and Relationships — A. This was a <Title>, which at the time
                  always rendered an <h1>, so it put a level-one heading reading
                  "Remember me" inside a <label> — invalid nesting, and announced as a
                  page heading. Title now takes an explicit level, but a checkbox label
                  is still not a heading at any level: replaced with a span carrying the
                  same visual classes.
                */}
                    <span className="mb-0 text-xs font-medium leading-normal">
                      Remember me
                    </span>
                  </label>
                )}
              </form.Field>

              <Button
                type="button"
                variant="link"
                className="p-0 text-xs"
                onClick={onForgotPassword}
                data-testid={IDENTIFIERS.LOGIN.FORGOT_PASSWORD_LINK}
                logTag={LogTags.ACCOUNT_ACCESS}
                logCategory={LogCategories.LOGIN}
              >
                Forgot Password?
              </Button>
            </div>

            <div className="flex w-full justify-between gap-6">
              {/* login btn */}
              <div className="">
                <form.Subscribe
                  selector={(state) => [
                    state.canSubmit,
                    state.values.userName,
                    state.values.password,
                  ]}
                >
                  {([canSubmit, userName, password]) => {
                    // 3.3.1 Error Identification — A. Was `disabled={!canSubmit || …}` —
                    // native disabled removes the button from the tab order entirely, so
                    // a keyboard user could never reach it to find out why it wasn't
                    // working. aria-disabled keeps the same dimmed look (reproduced
                    // directly in className, since disabled:opacity-50 no longer
                    // applies) without removing it from the tab order.
                    //
                    // aria-disabled support for actually *announcing* "disabled" varies
                    // across screen reader/browser pairings — some are reliable, some
                    // stay silent on it — so the state isn't left to that alone: the
                    // description names the specific empty field(s) rather than a
                    // static list, so it stays accurate as the member fills the form
                    // in, and it's spoken every time regardless of whether the AT also
                    // surfaces the ARIA property. Because the button stays genuinely
                    // clickable, clicking it while invalid runs the field validators
                    // (TanStack Form evaluates all of them on a submit attempt
                    // regardless of lifecycle), surfacing the specific missing field
                    // under that field itself. Native `disabled` is kept for the one
                    // case where fully blocking it is correct: an actual request
                    // already in flight.
                    const requirement =
                      describeMissingFields([
                        { label: "username", value: userName },
                        { label: "password", value: password },
                      ]) || "Disabled. Fix the errors above to continue.";
                    return (
                      <>
                        <Button
                          size="lg"
                          variant="default"
                          className={cn(
                            "bg-primary hover:bg-primary-selected",
                            !canSubmit && "opacity-50",
                          )}
                          type="submit"
                          disabled={loginQuery.isPending}
                          aria-disabled={!canSubmit}
                          aria-describedby={
                            !canSubmit ? "login-button-requirement" : undefined
                          }
                          data-testid={IDENTIFIERS.LOGIN.LOGIN_BUTTON}
                        >
                          {loginQuery.isPending ? "Logging In ..." : "Login"}
                        </Button>
                        {!canSubmit && (
                          <p id="login-button-requirement" className="sr-only">
                            {requirement}
                          </p>
                        )}
                      </>
                    );
                  }}
                </form.Subscribe>
              </div>

              <div className="">
                <Button
                  size="lg"
                  type="button"
                  variant="secondary"
                  className="bg-primary text-sm text-white hover:bg-primary-selected"
                  onClick={onRegister}
                  data-testid={IDENTIFIERS.LOGIN.REGISTER_BUTTON}
                >
                  Register
                </Button>
              </div>
            </div>
          </form>
          <footer className="absolute bottom-0 left-0 flex w-full items-center justify-center overflow-hidden p-4">
            <Button
              variant="link"
              onClick={openPanel}
              data-testid={IDENTIFIERS.LOGIN.NEED_HELP_LINK}
            >
              Need Help?
            </Button>
          </footer>
        </div>
      )}
    </main>
  );
};

export default LoginPage;
